import { DownloadOutlined, ReloadOutlined, TableOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Empty, Modal, Table, Tabs, message } from 'antd';
import moment from 'moment';
import { ReactNode, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import PageTitle from 'apps/home/components/PageTitle';
import apiCall from 'apps/robo-advisory/apiCalls/portfolio-analysis';
import portfolioApiCall from 'apps/robo-advisory/apiCalls/portfolio';
import portfolioDailyApiCall, {
  backfillPortfolioDailyApiCall,
} from 'apps/robo-advisory/apiCalls/portfolio-daily';
import portfolioNavPerformanceApiCall from 'apps/robo-advisory/apiCalls/portfolio-nav-performance';
import portfolioStockApiCall from 'apps/robo-advisory/apiCalls/portfolioStock';
import portfolioDriftApiCall from 'apps/robo-advisory/apiCalls/portfolio-drift';
import { DataTable } from 'common/data-table';
import ErrorMessage from 'common/ui/ErrorMessage';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from 'common/ui/chart';
import { getChangeValueColor } from 'utils/color';
import { currencyCell, numberCell } from 'utils/cell-formatter';
import { formatCurrency, formatNumber } from 'utils/formatAmount';

import { portfolioStockColumns } from './config';

// Compact axis labels for VND amounts: 1.2 tỷ / 850 tr / 12,000, so large
// portfolio values don't crowd the Y axis with full currency strings.
function formatCompactVND(value: number) {
  if (value == null || isNaN(value)) return '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)} tỷ`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(0)} tr`;
  return formatNumber(value);
}

const PortfolioValueTooltip = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: any }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const point = payload[0]?.payload || {};
  return (
    <div className="rounded border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-gray-800">
        {moment(label).format('DD/MM/YYYY')}
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-600">Giá trị danh mục</span>
        <span className="font-mono font-semibold">
          {formatCurrency(point.market_value)}
        </span>
      </div>
      {!point.is_actual && (
        <div className="mt-1 text-[11px] text-gray-400">
          Giá tham chiếu từ ngày giao dịch gần nhất
        </div>
      )}
    </div>
  );
};

function PortfolioValueChart({ points }) {
  if (!points || points.length < 2) {
    return <Empty description="Không đủ dữ liệu để hiển thị biểu đồ" />;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={points} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="portfolioValueFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#1971c2" stopOpacity={0.25} />
            <stop offset="95%" stopColor="#1971c2" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickFormatter={(value) => moment(value).format('DD/MM/YY')}
          minTickGap={48}
        />
        <YAxis
          domain={['auto', 'auto']}
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickFormatter={formatCompactVND}
          width={64}
        />
        <RechartsTooltip content={<PortfolioValueTooltip />} />
        <Area
          type="monotone"
          dataKey="market_value"
          stroke="#1971c2"
          strokeWidth={2}
          fill="url(#portfolioValueFill)"
          dot={false}
          activeDot={{ r: 4 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

const PORTFOLIO_INDEX_COLOR = '#1971c2';
const BENCHMARK_INDEX_COLOR = '#e03131';

const NavPerformanceTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: any[];
  label?: any;
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const point = payload[0]?.payload || {};

  const renderIndex = (name: string, value: number | null | undefined, color: string) => {
    if (value === null || value === undefined) return null;
    const changePct = value - 100;
    return (
      <div className="flex items-center justify-between gap-4" key={name}>
        <span className="flex items-center gap-1.5 text-gray-600">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
          {name}
        </span>
        <span className="font-mono font-semibold">
          {formatNumber(value)}{' '}
          <span style={{ color: getChangeValueColor(changePct) }}>
            ({changePct >= 0 ? '+' : ''}
            {formatNumber(changePct)}%)
          </span>
        </span>
      </div>
    );
  };

  return (
    <div className="rounded border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-gray-800">{moment(label).format('DD/MM/YYYY')}</div>
      {renderIndex('Danh mục', point.portfolio_index, PORTFOLIO_INDEX_COLOR)}
      {renderIndex('VNINDEX', point.benchmark_index, BENCHMARK_INDEX_COLOR)}
      {point.market_value !== undefined && (
        <div className="mt-1 flex items-center justify-between gap-4 border-t border-gray-100 pt-1 text-gray-500">
          <span>Giá trị danh mục</span>
          <span className="font-mono">{formatCurrency(point.market_value)}</span>
        </div>
      )}
    </div>
  );
};

const navTableColumns = [
  {
    title: 'Ngày',
    dataIndex: 'date',
    render: (value) => moment(value).format('DD/MM/YYYY'),
  },
  {
    title: 'Giá trị danh mục',
    dataIndex: 'market_value',
    align: 'right' as const,
    render: (value) => <span className="font-mono">{formatCurrency(value)}</span>,
  },
  {
    title: 'NAV (Danh mục)',
    dataIndex: 'portfolio_index',
    align: 'right' as const,
    render: (value) => (
      <span className="font-mono" style={{ color: getChangeValueColor(value - 100) }}>
        {formatNumber(value)}
      </span>
    ),
  },
  {
    title: 'VNINDEX',
    dataIndex: 'benchmark_index',
    align: 'right' as const,
    render: (value) =>
      value === null || value === undefined ? (
        '—'
      ) : (
        <span className="font-mono" style={{ color: getChangeValueColor(value - 100) }}>
          {formatNumber(value)}
        </span>
      ),
  },
];

// NAV (TWR performance index, base 100) of the portfolio vs. VNINDEX, both
// normalized to 100 at the series start, plus a "Bảng dữ liệu" modal listing
// every underlying portfolio_daily row (market value + NAV index) behind the chart.
function NavPerformanceChart({ points }) {
  const [isTableOpen, setIsTableOpen] = useState(false);

  if (!points || points.length < 2) {
    return <Empty description="Không đủ dữ liệu để hiển thị biểu đồ" />;
  }

  const exportCsv = () => {
    const header = 'date,market_value,portfolio_index,benchmark_index';
    const rows = points.map((p) =>
      [p.date, p.market_value, p.portfolio_index, p.benchmark_index ?? ''].join(',')
    );
    const blob = new Blob(['﻿' + [header, ...rows].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'portfolio-nav-performance.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const lastPoint = points[points.length - 1];
  const lastBenchmark = [...points].reverse().find((p) => p.benchmark_index != null);

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4 text-sm">
          <span>
            Danh mục:{' '}
            <span
              className="font-semibold"
              style={{ color: getChangeValueColor(lastPoint.portfolio_index - 100) }}
            >
              {lastPoint.portfolio_index >= 100 ? '+' : ''}
              {formatNumber(lastPoint.portfolio_index - 100)}%
            </span>
          </span>
          {lastBenchmark && (
            <span>
              VNINDEX:{' '}
              <span
                className="font-semibold"
                style={{ color: getChangeValueColor(lastBenchmark.benchmark_index - 100) }}
              >
                {lastBenchmark.benchmark_index >= 100 ? '+' : ''}
                {formatNumber(lastBenchmark.benchmark_index - 100)}%
              </span>
            </span>
          )}
        </div>
        <Button icon={<TableOutlined />} onClick={() => setIsTableOpen(true)}>
          Bảng dữ liệu
        </Button>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={points} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={(value) => moment(value).format('DD/MM/YY')}
            minTickGap={48}
          />
          <YAxis
            domain={['auto', 'auto']}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickFormatter={(value) => formatNumber(value)}
            width={56}
          />
          <RechartsTooltip content={<NavPerformanceTooltip />} />
          <Legend
            formatter={(value) => (value === 'portfolio_index' ? 'Danh mục' : 'VNINDEX')}
            wrapperStyle={{ fontSize: 12 }}
          />
          <ReferenceLine y={100} stroke="#9ca3af" strokeDasharray="4 4" />
          <Line
            type="monotone"
            dataKey="portfolio_index"
            stroke={PORTFOLIO_INDEX_COLOR}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
          <Line
            type="monotone"
            dataKey="benchmark_index"
            stroke={BENCHMARK_INDEX_COLOR}
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-3 rounded bg-gray-50 px-3 py-2 text-xs text-gray-500 leading-relaxed">
        <span className="font-medium text-gray-600">Cách tính: </span>
        Hiệu suất tính theo phương pháp Time-Weighted Return (TWR): lợi suất
        từng ngày được tính sau khi loại trừ tiền mua/bán trong ngày, rồi nhân
        chuỗi lại — nhờ đó các giao dịch mua thêm định kỳ không bị tính nhầm
        là tăng trưởng. Cả hai đường đều chuẩn hóa = 100 tại ngày bắt đầu.
      </div>
      <Modal
        title="Dữ liệu danh mục theo ngày"
        open={isTableOpen}
        onCancel={() => setIsTableOpen(false)}
        footer={
          <Button icon={<DownloadOutlined />} onClick={exportCsv}>
            Xuất CSV
          </Button>
        }
        width={760}
      >
        <Table
          rowKey="date"
          columns={navTableColumns}
          dataSource={[...points].reverse()}
          size="small"
          pagination={{ pageSize: 24, showSizeChanger: false }}
        />
      </Modal>
    </>
  );
}

const qtyCell = ({ getValue }) => {
  const value = getValue();
  return (
    <div className="text-right">
      {value == null || isNaN(value) ? '-' : Math.ceil(value).toLocaleString('en-US')}
    </div>
  );
};

const SLICE_COLORS = [
  '#e03131',
  '#1971c2',
  '#f59f00',
  '#2f9e44',
  '#ae3ec9',
  '#e8590c',
  '#0c8599',
  '#c2255c',
  '#6741d9',
  '#74b816',
];

const symbolColumns = [
  { header: 'Mã CK', accessorKey: 'symbol' },
  { header: 'Tên', accessorKey: 'name' },
  {
    header: () => <div className="text-right">Số lượng</div>,
    accessorKey: 'quantity',
    cell: qtyCell,
  },
  {
    header: () => <div className="text-right">Giá trị thị trường</div>,
    accessorKey: 'market_value',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">Tỷ trọng (%)</div>,
    accessorKey: 'allocation_percentage',
    cell: numberCell,
  },
];

const industryNameCell = ({ getValue, row }) => (
  <div className="flex items-center gap-2">
    <span
      className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
      style={{ backgroundColor: SLICE_COLORS[row.index % SLICE_COLORS.length] }}
    />
    {getValue()}
  </div>
);

const industryColumns = [
  { header: 'Ngành', accessorKey: 'industry_name', cell: industryNameCell },
  {
    header: () => <div className="text-right">Số mã CK</div>,
    accessorKey: 'stock_count',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Giá trị thị trường</div>,
    accessorKey: 'market_value',
    cell: currencyCell,
  },
  {
    header: () => <div className="text-right">Tỷ trọng (%)</div>,
    accessorKey: 'allocation_percentage',
    cell: numberCell,
  },
];

const driftValueCell = ({ getValue }) => {
  const value = getValue();
  if (value == null) return <div className="text-right">-</div>;
  const colorClass = Math.abs(value) < 0.01 ? 'text-gray-400' : value > 0 ? 'text-amber-600' : 'text-blue-600';
  const sign = value > 0 ? '+' : '';
  return (
    <div className={`text-right font-medium ${colorClass}`}>
      {sign}{value.toFixed(2)}%
    </div>
  );
};

const ACTION_DRIFT_THRESHOLD = 1;

// Translate each stock's drift into a directional buy/sell suggestion: an
// overweight position (positive drift) should be trimmed, an underweight one
// (negative drift) should be topped up. The suggested amount is the market
// value gap needed to close the drift, derived purely from data already on
// the drift response. Drifts smaller than the threshold need no action.
// admincp has no order-creation flow (that only exists in the wealth app),
// so this is informational only, merged straight into the holdings table.
function withSuggestedAction(items, totalMarketValue) {
  return (items || []).map((item) => {
    if (Math.abs(item.drift_percentage) < ACTION_DRIFT_THRESHOLD) {
      return { ...item, suggested_action: null, suggested_amount: null };
    }
    return {
      ...item,
      suggested_action: item.drift_percentage > 0 ? 'Bán' : 'Mua',
      suggested_amount: (Math.abs(item.drift_percentage) / 100) * totalMarketValue,
    };
  });
}

const actionBadgeCell = ({ getValue, row }) => {
  const action = getValue();
  if (!action) return <span className="text-xs text-gray-400">—</span>;
  const isBuy = action === 'Mua';
  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
          isBuy ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
        }`}
      >
        {action}
      </span>
      {row.original.suggested_amount != null && (
        <span className="text-xs text-gray-500">
          {formatCurrency(row.original.suggested_amount)}
        </span>
      )}
    </div>
  );
};

const symbolDriftColumns = [
  { header: 'Mã CK', accessorKey: 'symbol' },
  {
    header: () => <div className="text-right">Mục tiêu (%)</div>,
    accessorKey: 'target_percentage',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Hiện tại (%)</div>,
    accessorKey: 'current_percentage',
    cell: numberCell,
  },
  {
    header: () => <div className="text-right">Chênh lệch</div>,
    accessorKey: 'drift_percentage',
    cell: driftValueCell,
  },
  { header: 'Đề xuất', accessorKey: 'suggested_action', cell: actionBadgeCell },
];

const StatCard = ({
  title,
  value,
  valueClass = '',
  to,
}: {
  title?: ReactNode;
  value?: ReactNode;
  valueClass?: string;
  to?: string;
}) => {
  const navigate = useNavigate();
  const isClickable = !!to;

  return (
    <div
      className={`flex flex-col items-center px-1 py-1 border border-gray-200 rounded-md transition
        ${isClickable ? 'cursor-pointer hover:shadow hover:border-blue-400 bg-blue-50' : ''}
      `}
      onClick={isClickable ? () => navigate(to) : undefined}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? 'button' : undefined}
      aria-label={isClickable ? `Xem chi tiết ${title}` : undefined}
    >
      <span className={`font-bold ${valueClass} text-base`}>{value}</span>
      <span className="text-[11px] text-gray-400 mt-0.5">{title}</span>
    </div>
  );
};

function StockAllocationChart({ stocks }) {
  const chartable = (stocks || []).filter((s) => s.market_value != null && s.market_value > 0);
  const totalMarketValue = chartable.reduce((sum, s) => sum + s.market_value, 0);
  if (!chartable.length || totalMarketValue <= 0) {
    return null;
  }

  const data = chartable.map((s) => ({
    symbol: s.stock_symbol,
    value: parseFloat(((s.market_value / totalMarketValue) * 100).toFixed(2)),
  }));

  const config: ChartConfig = Object.fromEntries(
    data.map((d, i) => [
      d.symbol,
      { label: `${d.symbol} (${d.value.toFixed(2)}%)`, color: SLICE_COLORS[i % SLICE_COLORS.length] },
    ])
  );

  return (
    <ChartContainer config={config} className="mx-auto h-72 w-full max-w-sm">
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="symbol"
          cx="50%"
          cy="50%"
          innerRadius="55%"
          outerRadius="80%"
          paddingAngle={2}
          strokeWidth={0}
        >
          {data.map((d, i) => (
            <Cell key={d.symbol} fill={SLICE_COLORS[i % SLICE_COLORS.length]} />
          ))}
        </Pie>
        <ChartTooltip content={<ChartTooltipContent nameKey="symbol" />} />
      </PieChart>
    </ChartContainer>
  );
}

function IndustryAllocationChart({ byIndustry }) {
  if (!byIndustry || byIndustry.length === 0) {
    return null;
  }

  const config: ChartConfig = Object.fromEntries(
    byIndustry.map((item, i) => [
      item.industry_name,
      {
        label: `${item.industry_name} (${item.allocation_percentage.toFixed(2)}%)`,
        color: SLICE_COLORS[i % SLICE_COLORS.length],
      },
    ])
  );

  // Legend is dropped in favor of the colored swatches in the table below.
  return (
    <ChartContainer config={config} className="mx-auto h-72 w-full max-w-sm">
      <PieChart>
        <Pie
          data={byIndustry}
          dataKey="allocation_percentage"
          nameKey="industry_name"
          cx="50%"
          cy="50%"
          innerRadius="55%"
          outerRadius="80%"
          paddingAngle={2}
          strokeWidth={0}
        >
          {byIndustry.map((item, i) => (
            <Cell
              key={item.industry_code}
              fill={SLICE_COLORS[i % SLICE_COLORS.length]}
            />
          ))}
        </Pie>
        <ChartTooltip content={<ChartTooltipContent nameKey="industry_name" />} />
      </PieChart>
    </ChartContainer>
  );
}

// Too many symbols to fit a legend without breaking the layout; rely on the
// tooltip for identification instead.
function SymbolAllocationChart({ bySymbol }) {
  if (!bySymbol || bySymbol.length === 0) {
    return null;
  }

  const config: ChartConfig = Object.fromEntries(
    bySymbol.map((item, i) => [
      item.symbol,
      {
        label: `${item.symbol} (${item.allocation_percentage.toFixed(2)}%)`,
        color: SLICE_COLORS[i % SLICE_COLORS.length],
      },
    ])
  );

  return (
    <ChartContainer config={config} className="mx-auto h-72 w-full max-w-sm">
      <PieChart>
        <Pie
          data={bySymbol}
          dataKey="allocation_percentage"
          nameKey="symbol"
          cx="50%"
          cy="50%"
          innerRadius="55%"
          outerRadius="80%"
          paddingAngle={2}
          strokeWidth={0}
        >
          {bySymbol.map((item, i) => (
            <Cell key={item.symbol} fill={SLICE_COLORS[i % SLICE_COLORS.length]} />
          ))}
        </Pie>
        <ChartTooltip content={<ChartTooltipContent nameKey="symbol" />} />
      </PieChart>
    </ChartContainer>
  );
}

export const PortfolioAnalysisPage = () => {
  const { user_id } = useParams();
  const queryClient = useQueryClient();

  const { mutate: backfillPortfolioDaily, isPending: isBackfilling } = useMutation({
    mutationFn: () => backfillPortfolioDailyApiCall.queryFn({ user_id }),
    onSuccess: () => {
      message.success('Đã backfill dữ liệu danh mục theo ngày');
      queryClient.invalidateQueries({ queryKey: [portfolioDailyApiCall.queryKey, user_id] });
      queryClient.invalidateQueries({
        queryKey: [portfolioNavPerformanceApiCall.queryKey, user_id],
      });
    },
    onError: err => {
      message.error(err?.message || 'Backfill dữ liệu danh mục theo ngày thất bại');
    },
  });

  const { data: response, isLoading, error: fetchError } = useQuery({
    queryKey: [apiCall.queryKey, user_id],
    queryFn: () => apiCall.queryFn({ user_id }),
  });

  const { data: summaryResponse } = useQuery({
    queryKey: [portfolioApiCall.detail.queryKey, user_id],
    queryFn: () => portfolioApiCall.detail.queryFn({ user_id }),
    enabled: !!user_id,
  });

  const { data: stocksResponse } = useQuery({
    queryKey: [portfolioStockApiCall.list.queryKey, user_id],
    queryFn: () => portfolioStockApiCall.list.queryFn({ user_id }),
    enabled: !!user_id,
  });

  const { data: driftResponse } = useQuery({
    queryKey: [portfolioDriftApiCall.queryKey, user_id],
    queryFn: () => portfolioDriftApiCall.queryFn({ user_id }),
    enabled: !!user_id,
  });

  const { data: dailyResponse } = useQuery({
    queryKey: [portfolioDailyApiCall.queryKey, user_id],
    queryFn: () => portfolioDailyApiCall.queryFn({ user_id }),
    enabled: !!user_id,
  });

  const { data: navPerformanceResponse } = useQuery({
    queryKey: [portfolioNavPerformanceApiCall.queryKey, user_id],
    queryFn: () => portfolioNavPerformanceApiCall.queryFn({ user_id }),
    enabled: !!user_id,
  });

  const breakdown = response?.data;
  const summary = Array.isArray(summaryResponse?.data)
    ? summaryResponse.data[0]
    : summaryResponse?.data || summaryResponse;
  const stocks = stocksResponse?.data || [];
  const drift = driftResponse?.data;
  const dailyPoints = dailyResponse?.data || [];
  const navPerformancePoints = navPerformanceResponse?.data || [];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (fetchError) {
    return <ErrorMessage {...fetchError} />;
  }

  const isPositive = (summary?.unrealized_gain_loss_amount ?? 0) >= 0;
  const trendColor = isPositive ? 'text-green-500' : 'text-red-500';

  return (
    <>
      <PageTitle
        title="Portfolio Analysis"
        backUrl={`/robo-advisory/portfolios/${user_id}`}
      />

      {summary && (
        <Card className="my-4 shadow-md" title="Tổng quan danh mục">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <StatCard
              title="Giá trị danh mục"
              value={formatCurrency(summary?.market_value)}
              valueClass={trendColor}
            />
            <StatCard
              title="Lãi/Lỗ"
              value={
                <span>
                  {formatCurrency(summary?.unrealized_gain_loss_amount)}
                  <span className="text-xs">
                    ({formatNumber(summary?.unrealized_gain_loss_percentage)}%)
                  </span>
                </span>
              }
              valueClass={trendColor}
            />
            <StatCard
              title="Vốn mua"
              value={formatCurrency(summary?.buy_value)}
              valueClass="text-blue-600"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard title="Tổng đã mua" value={formatCurrency(summary?.total_buy_amount)} />
            <StatCard title="Tổng đã bán" value={formatCurrency(summary?.total_sell_amount)} />
            <StatCard title="Mua chưa khớp" value={formatCurrency(summary?.total_pending_buy_amount)} />
            <StatCard title="Bán chưa khớp" value={formatCurrency(summary?.total_pending_sell_amount)} />
          </div>
        </Card>
      )}

      <Card
        className="my-4 shadow-md"
        title="Giá trị danh mục theo thời gian"
        extra={
          <Button
            icon={<ReloadOutlined />}
            loading={isBackfilling}
            onClick={() => backfillPortfolioDaily()}
          >
            Backfill dữ liệu theo ngày
          </Button>
        }
      >
        <Tabs
          items={[
            {
              key: 'market_value',
              label: 'Giá trị danh mục',
              children: <PortfolioValueChart points={dailyPoints} />,
            },
            {
              key: 'nav',
              label: 'NAV vs VNINDEX',
              children: <NavPerformanceChart points={navPerformancePoints} />,
            },
          ]}
        />
      </Card>

      <Card className="my-4 shadow-md" title="Danh mục chứng khoán">
        <StockAllocationChart stocks={stocks} />
        <DataTable
          columns={portfolioStockColumns}
          data={stocks}
          tableName="portfolioAnalysisStockHoldings"
          noViewOptions={true}
        />
      </Card>

      <Card className="my-4 shadow-md" title="Phân bổ theo ngành">
        <IndustryAllocationChart byIndustry={breakdown?.by_industry} />
        <DataTable
          columns={industryColumns}
          data={breakdown?.by_industry || []}
          tableName="portfolioAnalysisByIndustry"
          noViewOptions={true}
        />
      </Card>

      <Card className="my-4 shadow-md" title="Phân bổ theo mã cổ phiếu">
        <SymbolAllocationChart bySymbol={breakdown?.by_symbol} />
        <DataTable
          columns={symbolColumns}
          data={breakdown?.by_symbol || []}
          tableName="portfolioAnalysisBySymbol"
          noViewOptions={true}
        />
      </Card>

      <Card
        className="my-4 shadow-md"
        title="Danh mục mục tiêu"
        extra={
          <Link
            to={`/robo-advisory/portfolios/${user_id}/target`}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            {drift?.has_target ? 'Chỉnh sửa mục tiêu →' : 'Thiết lập mục tiêu →'}
          </Link>
        }
      >
        {!drift?.has_target ? (
          <p className="py-6 text-center text-gray-500">
            Chưa thiết lập danh mục mục tiêu cho khách hàng này.
          </p>
        ) : (
          <DataTable
            columns={symbolDriftColumns}
            data={withSuggestedAction(drift.by_symbol, drift.total_market_value)}
            tableName="portfolioDriftBySymbol"
            noViewOptions={true}
          />
        )}
      </Card>
    </>
  );
};
